import { useState } from 'react';
import Loader from '../../common/Loader';
import { Button, Grid, Box, Typography, Chip } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { FormProvider, RHFSelectField, RHFTextField } from '../../common/hook-form';
import RHFDateRangePickerField2 from '../../common/hook-form/RHFDatePicker2';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { apiConstants, postGeneric } from '../../common/constants';
import _ from 'lodash';
import { formatDate } from '../../helpers/UtilityHelper';
import MUIModal from '../../common/MUIModal';
import { getMerchant } from '../../helpers/ReduxHelpers';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CallKpiCards, CallKpiSkeleton, CallOutcomeTabs } from '../ndr/CallStatsUI';
import { primaryTextSx, secondaryTextSx, awbTextSx, cellStackSx } from '../orders/list2/cellStyles';
import { filterBarSx, primaryBtnSx, secondaryBtnSx } from '../../styles/patterns';
import { FONT, SIZE } from '../../styles/foundation/typography';
import { SUCCESS, ERROR, SUBTLE, PRIMARY_BLUE, WARNING } from '../../styles/foundation/colors';
import AwbLinkCell from '../../common/AwbLinkCell';

const NDRCall = () => {
    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState();
    const [data, setData] = useState();
    const [sizePerPage, setSizePerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [ndrQ, setNdrQ] = useState([])
    const [ndrQModal, setNdrQModal] = useState(false)
    // Connected / Not Connected bifurcation — resolved server-side on npr_result.
    const [callTab, setCallTab] = useState('CONNECTED')
    const [analytics, setAnalytics] = useState(null)
    const [analyticsLoading, setAnalyticsLoading] = useState(true)
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const CALL_TABS = [
        { key: 'CONNECTED', label: 'Connected', icon: <PhoneInTalkIcon />, color: SUCCESS, help: 'Customer answered the call' },
        { key: 'NOT_CONNECTED', label: 'Not Connected', icon: <PhoneMissedIcon />, color: ERROR, help: 'Call did not connect' },
    ]

    const methods = useForm({
        defaultValues: {
            awbnumber: '',
            nprResults: '',
            callFromDate: '',
            callToDate: ''
        }
    });
    const {
        handleSubmit,
        reset,
    } = methods;

    const NpRResults = ["Reattempt Pickup", "Pickup Cancelled", "Picked Up", "Order Not Delivered", "Wrong Number",
        "Language Barrier", "Schedule Call Back", "Redial", "Busy or Improper Response", "Not Connected"]
    const options = NpRResults.map((op) => ({ label: _.startCase(op), value: op }))

    const handleFilterSubmit = (param) => {
        setFiltersChange(filterChange + 1)
        setFilters(param)
    }

    useEffect(() => {
        setPage(0)
    }, [sizePerPage, filters, callTab])

    useEffect(() => {
        setLoading(true)
        const filtersToApply = [{ field: "merchantOid", operator: "eq", value: merchantId }];
        if (filters?.awbnumber) {
            filtersToApply.push({ field: "awb_number", operator: "eq", value: filters.awbnumber });
        }
        if (filters?.nprResults) {
            filtersToApply.push({ field: "npr_result", operator: "eq", value: filters.nprResults });
        }
        // Date range on call time
        if (filters?.callFromDate) {
            filtersToApply.push({ field: "call_start_time", operator: "gte", value: formatDate(filters.callFromDate, 'yyyy-MM-dd') + 'T00:00:00' });
        }
        if (filters?.callToDate) {
            filtersToApply.push({ field: "call_start_time", operator: "lte", value: formatDate(filters.callToDate, 'yyyy-MM-dd') + 'T23:59:59' });
        }
        // Connected / Not Connected tab (bifurcation on npr_result)
        if (callTab === 'CONNECTED') {
            filtersToApply.push({ field: "npr_result", operator: "ne", value: "Not Connected" });
        } else {
            filtersToApply.push({ field: "npr_result", operator: "eq", value: "Not Connected" });
        }
        const payload = {
            // "collection": "futworks_npr_recording",
            "offset": page * sizePerPage,
            "limit": sizePerPage,
            "skipCount": true,
            "countOnly": false,
            "sort": {
                "direction": "DESC",
                "field": "created_at"
            }, "andfilter": filtersToApply
        }
        postGeneric(apiConstants.NPR_RECORDING_SEARCH, payload).then((res) => {
            setLoading(false)
            setData(res?.data?.data)
        }).catch((err) => {
            setLoading(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizePerPage, page, filterChange, filters, callTab])

    // NPR call analytics (last 30 days) for the KPI cards.
    useEffect(() => {
        if (!merchantId) return;
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        const fmt = (d) => d.toISOString().slice(0, 10);
        setAnalyticsLoading(true);
        postGeneric('intervention/calls/analytics', {
            type: "NPR",
            merchantOids: [merchantId],
            dateRange: { from: fmt(thirtyDaysAgo), to: fmt(today) },
        }).then((res) => {
            setAnalytics(res?.data);
        }).catch(() => {
            setAnalytics(null);
        }).finally(() => setAnalyticsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantId])

    // KPI computations
    const totalCalls = analytics?.totalCalls || 0;
    const connected = analytics?.connected || 0;
    const connectRate = analytics?.connectRate || 0;
    const avgConnected = connected > 0 ? Math.round((analytics?.avgDuration_seconds || 0) * totalCalls / connected) : 0;
    const avgDurDisplay = avgConnected >= 60 ? `${Math.floor(avgConnected / 60)}m ${avgConnected % 60}s` : `${avgConnected}s`;
    const o = analytics?.outcomeDistribution || {};
    const reattemptCount = (o['Reattempt Pickup'] || 0) + (o['Schedule Call Back'] || 0);
    const cancelledCount = (o['Pickup Cancelled'] || 0);
    const pickedUpCount = (o['Picked Up'] || 0);
    const notConnectedCount = (o['Not Connected'] || 0);
    const busyCount = (o['Busy or Improper Response'] || 0);
    const otherCount = (o['Wrong Number'] || 0) + (o['Language Barrier'] || 0) + (o['Redial'] || 0) + (o['Order Not Delivered'] || 0);

    const summaryCards = [
        { label: 'Total Calls', value: totalCalls.toLocaleString(), color: PRIMARY_BLUE, icon: <CallOutlinedIcon /> },
        { label: 'Connected', value: connected.toLocaleString(), sub: `${connectRate}% connect rate`, color: SUCCESS, icon: <PhoneInTalkIcon /> },
        { label: 'Avg Duration (Connected)', value: avgDurDisplay, color: PRIMARY_BLUE, icon: <AccessTimeIcon /> },
    ];
    const dispositionCards = [
        { label: 'Reattempt', value: reattemptCount.toLocaleString(), color: SUCCESS },
        { label: 'Cancelled', value: cancelledCount.toLocaleString(), color: ERROR },
        { label: 'Picked Up', value: pickedUpCount.toLocaleString(), color: SUCCESS },
        { label: 'Not Connected', value: notConnectedCount.toLocaleString(), color: ERROR },
        { label: 'Busy', value: busyCount.toLocaleString(), color: WARNING },
        { label: 'Other', value: otherCount.toLocaleString(), color: SUBTLE },
    ];
    // Connected / Not Connected are complementary; derive the not-connected count for the tab badges.
    const notConnectedTotal = Math.max(totalCalls - connected, 0);
    const callTabs = CALL_TABS.map((t) => ({
        ...t,
        count: (t.key === 'CONNECTED' ? connected : notConnectedTotal).toLocaleString(),
    }));

    const columns = [
        {
            field: 'awb_number', headerAlign: "left", hideSortIcons: true, headerName: 'AWB No.', flex: 0.8, minWidth: 150, renderCell: (params) => {
                const { row } = params;
                return (
                    <AwbLinkCell awb={row.awb_number} sx={awbTextSx} />
                )
            },
        },
        {
            field: 'npr_result', headerAlign: "left", hideSortIcons: true, headerName: 'NPR Results', flex: 1, minWidth: 160, renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>{row.npr_result}</Typography>
                )
            },
        },
        {
            field: 'call_start_time', headerAlign: "left", hideSortIcons: true, headerName: 'Call Time', flex: 1.1, minWidth: 170, renderCell: (params) => {
                const { row } = params;
                return <Typography sx={secondaryTextSx}>{formatDate(row.call_start_time)}</Typography>
            },
        },
        {
            field: 'call_duration', headerAlign: "left", hideSortIcons: true, headerName: 'Call Duration', flex: 1, minWidth: 130, renderCell: (params) => {
                const { row } = params;
                const dur = row?.call_duration;
                if (dur == null || dur === 0) return <Typography sx={secondaryTextSx}>-</Typography>;
                const mins = Math.floor(dur);
                const secs = Math.round((dur - mins) * 60);
                return <Typography sx={secondaryTextSx}>{mins} min, {secs} sec</Typography>
            },
        },
        {
            field: 'npr_outcome', headerAlign: "left", hideSortIcons: true, headerName: 'Outcome', flex: 1, minWidth: 110, renderCell: (params) => {
                const { row } = params;
                const outcome = row?.npr_outcome === true ? 'TRUE' : 'FALSE'
                return (
                    <Chip
                        size="small"
                        label={outcome}
                        sx={{
                            fontFamily: FONT,
                            fontSize: 11,
                            fontWeight: 600,
                            height: 24,
                            bgcolor: row?.npr_outcome ? SUCCESS + '18' : ERROR + '18',
                            color: row?.npr_outcome ? SUCCESS : ERROR,
                            border: '1px solid ' + (row?.npr_outcome ? SUCCESS + '40' : ERROR + '40'),
                            '& .MuiChip-label': { px: 1 }
                        }}
                    />
                )
            },
        },
        {
            field: 'recording_url', headerAlign: "left", hideSortIcons: true, headerName: 'Recordings', flex: 1, minWidth: 100,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <a href={row.recording_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3783e7', fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                        {row.recording_url ? "Play" : "-"}
                    </a>
                )
            },
        },
        {
            field: 'call_responses', align: 'center', headerAlign: "center", hideSortIcons: true, headerName: 'Call Response', flex: 1, minWidth: 150, renderCell: (params) => {
                const { row } = params;
                if (row?.call_responses && row.call_responses.length > 0) {
                    return (
                        <Button
                            sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', p: 0, minWidth: 0 }}
                            onClick={() => {
                                setNdrQ(row.call_responses);
                                setNdrQModal(true);
                            }}
                        >
                            View Responses
                        </Button>
                    )
                }
                else {
                    return ("-")
                }
            },
        }
    ];


    return (
        <>
            {/* KPI skeletons on first load, then real analytics cards */}
            {!analytics && analyticsLoading && <CallKpiSkeleton />}
            {analytics && <CallKpiCards summaryCards={summaryCards} dispositionCards={dispositionCards} />}

            {/* Connected / Not Connected tabs — self-describing so any user knows what they're seeing */}
            <CallOutcomeTabs tabs={callTabs} value={callTab} onChange={setCallTab} />

            <Box sx={filterBarSx}>
                <FormProvider methods={methods} onSubmit={handleSubmit(handleFilterSubmit)}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                            <RHFTextField name="awbnumber" label="AWB Number" size="small" />
                        </Box>
                        <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                            <RHFSelectField name="nprResults" label="NPR Results" width='100%' size="small" options={_.orderBy(options, 'value')} />
                        </Box>
                        <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                            <RHFDateRangePickerField2 label="Call Date" maxDate={new Date()} fromDateName="callFromDate" toDateName="callToDate" />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <Button type="submit" size="small" sx={primaryBtnSx}>Filter</Button>
                            <Button variant="outlined" size="small" sx={secondaryBtnSx} onClick={() => {
                                reset({
                                    awbnumber: '',
                                    nprResults: '',
                                    callFromDate: '',
                                    callToDate: '',
                                })
                                setFilters()
                                setFiltersChange((c) => c + 1)
                            }}>Reset</Button>
                        </Box>
                    </Box>
                </FormProvider>
            </Box>
            <Grid item xs={12}>
                {loading && <Loader />}
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data ? data.map((row, index) => ({ ...row, id: index + 1, })) : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    filterMode={"server"}
                    getRowId={(row) => row.id} />
            </Grid>
            <MUIModal
                open={ndrQModal}
                title="Call Details"
                maxWidth={"lg"}
                handleClose={() => setNdrQModal(false)}
            >
                <ProzoDataGrid
                    columns={[{
                        field: "question", headerName: "Question", flex: 3, minWidth: 300
                    }, {
                        field: "answer", flex: 1, headerName: "Answer", minWidth: 120
                    },]}
                    autoHeight={true}
                    rows={ndrQ || []}
                    pagination={false}
                    hideFooterPagination={true}
                    getRowId={(row) => Math.random()}
                />
            </MUIModal>
        </>
    );
}
export default NDRCall;
